import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiCalendar,
    FiCheckCircle,
    FiCreditCard,
    FiFileText,
    FiHash,
    FiLoader,
    FiPrinter,
    FiRefreshCw,
    FiUser,
    FiX,
    FiXCircle,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    clearPaymentAllocations,
    fetchPaymentAllocations,
    loadingAllocationsSelector,
    paymentAllocationErrorSelector,
    paymentAllocationsSelector,
} from "../../../../../../../store/billing/paymentAllocationSlice";

import "./SaleInvoicePaymentDetailsDialog.css";
import useAuth from "../../../../../../../hooks/useAuth";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const parsed =
        Number(value);

    return Number.isFinite(
        parsed
    )
        ? parsed
        : fallback;
};

const roundMoney = (
    value
) => {
    return (
        Math.round(
            (
                toNumber(
                    value
                ) +
                Number.EPSILON
            ) *
            100
        ) / 100
    );
};

const formatCurrency = (
    value,
    currency = "INR"
) => {
    return new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                currency ||
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(
            value
        )
    );
};

const formatDate = (
    value,
    includeTime = false
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        includeTime
            ? {
                day:
                    "2-digit",

                month:
                    "short",

                year:
                    "numeric",

                hour:
                    "2-digit",

                minute:
                    "2-digit",
            }
            : {
                day:
                    "2-digit",

                month:
                    "short",

                year:
                    "numeric",
            }
    ).format(
        date
    );
};

const formatLabel = (
    value
) => {
    if (!value) {
        return "-";
    }

    return String(
        value
    )
        .replace(
            /_/g,
            " "
        )
        .trim()
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );
};

const getTenantAddressText = (
    address
) => {
    if (!address) {
        return "-";
    }

    if (
        typeof address ===
        "string"
    ) {
        return (
            address.trim() ||
            "-"
        );
    }

    return [
        address.line1 ||
            address.addressLine1,
        address.line2 ||
            address.addressLine2,
        address.city,
        address.state,
        address.postalCode ||
            address.pincode,
        address.country,
    ]
        .filter(Boolean)
        .join(", ") ||
        "-";
};

const getPaymentId = (
    payment
) => {
    return (
        payment?._id ||
        payment?.paymentId ||
        payment?.payment?._id ||
        null
    );
};

const getPaymentStatus = (
    payment
) => {
    return String(
        payment?.paymentStatus ||
        payment?.status ||
        payment?.payment
            ?.paymentStatus ||
        payment?.payment?.status ||
        "ACTIVE"
    )
        .trim()
        .toUpperCase();
};

const getPaymentAmount = (
    payment
) => {
    const directAmount =
        toNumber(
            payment?.amount ??
            payment?.paymentAmount ??
            payment?.receivedAmount ??
            payment?.payment?.amount ??
            payment?.payment
                ?.paymentAmount ??
            0
        );

    if (
        directAmount >
        0
    ) {
        return roundMoney(
            directAmount
        );
    }

    return roundMoney(
        payment?.allocatedAmount ??
        payment?.activeAmount ??
        payment?.allocation
            ?.allocatedAmount ??
        payment?.allocation
            ?.activeAmount ??
        0
    );
};

const getAllocatedAmount = (
    payment,
    allocations = []
) => {
    const allocationTotal =
        allocations.reduce(
            (
                total,
                allocation
            ) =>
                total +
                toNumber(
                    allocation
                        ?.allocatedAmount ??
                    allocation
                        ?.activeAmount ??
                    0
                ),
            0
        );

    if (
        allocationTotal >
        0
    ) {
        return roundMoney(
            allocationTotal
        );
    }

    return roundMoney(
        payment?.allocatedAmount ??
        payment?.payment
            ?.allocatedAmount ??
        payment?.allocation
            ?.allocatedAmount ??
        payment?.activeAmount ??
        0
    );
};

const getReversedAmount = (
    payment,
    allocations = []
) => {
    const allocationTotal =
        allocations.reduce(
            (
                total,
                allocation
            ) =>
                total +
                toNumber(
                    allocation
                        ?.reversedAmount ??
                    0
                ),
            0
        );

    if (
        allocationTotal >
        0
    ) {
        return roundMoney(
            allocationTotal
        );
    }

    return roundMoney(
        payment?.reversedAmount ??
        payment?.payment
            ?.reversedAmount ??
        payment?.allocation
            ?.reversedAmount ??
        0
    );
};

const getUnallocatedAmount = (
    payment,
    allocatedAmount
) => {
    const directAmount =
        payment?.unallocatedAmount ??
        payment?.payment
            ?.unallocatedAmount;

    if (
        directAmount !==
        undefined &&
        directAmount !==
        null
    ) {
        return roundMoney(
            directAmount
        );
    }

    return roundMoney(
        Math.max(
            getPaymentAmount(
                payment
            ) -
            allocatedAmount,
            0
        )
    );
};

const getInvoiceNumber = (
    invoice,
    payment
) => {
    return (
        invoice?.documentNumber ||
        invoice?.invoiceNumber ||
        payment?.document
            ?.documentNumber ||
        payment?.documentNumber ||
        "-"
    );
};

const getCustomerName = (
    invoice,
    payment
) => {
    return (
        invoice?.customerSnapshot
            ?.customerName ||
        invoice?.customerSnapshot
            ?.name ||
        invoice?.customer?.name ||
        invoice?.customerName ||
        payment?.customerName ||
        "-"
    );
};

const getCustomerMobile = (
    invoice,
    payment
) => {
    return (
        invoice?.customerSnapshot
            ?.mobile ||
        invoice?.customer?.mobile ||
        invoice?.customerMobile ||
        payment?.customerMobile ||
        "-"
    );
};

/*
|--------------------------------------------------------------------------
| INFO FIELD
|--------------------------------------------------------------------------
*/

const PaymentInfoField = ({
    icon,
    label,
    value,
}) => {
    return (
        <div className="ckw-sipd-detail-field">
            <span className="ckw-sipd-detail-field-icon">
                {icon}
            </span>

            <div className="ckw-sipd-detail-field-content">
                <span className="ckw-sipd-detail-field-label">
                    {label}
                </span>

                <strong className="ckw-sipd-detail-field-value">
                    {value ||
                        "-"}
                </strong>
            </div>
        </div>
    );
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SaleInvoicePaymentDetailsDialog = ({
    open,
    payment,
    invoice,
    reversing = false,
    onClose,
    onPrint,
    onReverse,
    onAllocatePayment,
}) => {
    const { user } =
        useAuth();

    const printRef =
        useRef(null);

    const dispatch =
        useDispatch();

    const apiAllocations =
        useSelector(
            paymentAllocationsSelector
        ) || [];

    const loadingAllocations =
        useSelector(
            loadingAllocationsSelector
        );

    const allocationError =
        useSelector(
            paymentAllocationErrorSelector
        ) || "";

    const [
        showReverseForm,
        setShowReverseForm,
    ] = useState(false);

    const [
        reversalReason,
        setReversalReason,
    ] = useState("");

    const [
        reversalError,
        setReversalError,
    ] = useState("");

    const paymentId =
        getPaymentId(
            payment
        );

    /*
    |--------------------------------------------------------------------------
    | LOGGED-IN TENANT BUSINESS DETAILS
    |--------------------------------------------------------------------------
    */

    const tenant =
        user?.tenant || {};

    const tenantAddress =
        tenant?.address || {};

    const tenantBranding =
        tenant?.branding || {};

    const tenantSettings =
        tenant?.settings || {};

    const tenantLegalName =
        String(
            tenant?.legalName ||
            tenant?.name ||
            "BUSINESS"
        )
            .trim()
            .toUpperCase();

    const tenantMobile =
        tenant?.mobile ||
        "-";

    const tenantEmail =
        tenant?.email ||
        "-";

    const tenantGstin =
        tenant?.gstNumber ||
        "-";

    const tenantPan =
        tenant?.panNumber ||
        "-";

    const tenantAddressText =
        getTenantAddressText(
            tenantAddress
        );

    const tenantLogo =
        tenantBranding?.logo ||
        "";

    const tenantCurrency =
        String(
            tenantSettings?.currency ||
            "INR"
        )
            .trim()
            .toUpperCase();

    const displayCurrency =
        (
            value
        ) =>
            formatCurrency(
                value,
                tenantCurrency
            );

    /*
    |--------------------------------------------------------------------------
    | LOAD ALLOCATIONS
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        if (!open || !paymentId) {
            return undefined;
        }

        dispatch(
            fetchPaymentAllocations({
                paymentId,
                page: 1,
                limit: 100,
            })
        );

        return () => {
            dispatch(
                clearPaymentAllocations()
            );
        };
    }, [
        dispatch,
        open,
        paymentId,
    ]);

    /*
    |--------------------------------------------------------------------------
    | RESET LOCAL STATE
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            if (!open) {
                setShowReverseForm(
                    false
                );

                setReversalReason(
                    ""
                );

                setReversalError(
                    ""
                );
            }
        },
        [
            open,
        ]
    );

    const paymentStatus =
        getPaymentStatus(
            payment
        );

    const allocationStatus =
        String(
            payment?.allocationStatus ||
            payment?.status ||
            "ACTIVE"
        )
            .trim()
            .toUpperCase();

    const invoiceNumber =
        getInvoiceNumber(
            invoice,
            payment
        );

    const customerName =
        getCustomerName(
            invoice,
            payment
        );

    const customerMobile =
        getCustomerMobile(
            invoice,
            payment
        );

    const receiptNumber =
        payment?.receiptNumber ||
        payment?.paymentReceiptNumber ||
        payment?.payment
            ?.receiptNumber ||
        "-";

    const paymentDate =
        payment?.paymentDate ||
        payment?.payment
            ?.paymentDate ||
        payment?.allocationDate ||
        payment?.createdAt ||
        null;

    const createdAt =
        payment?.createdAt ||
        payment?.payment?.createdAt ||
        paymentDate;

    const paymentMethod =
        payment?.paymentMethod ||
        payment?.payment
            ?.paymentMethod ||
        "-";

    const referenceNumber =
        payment?.referenceNumber ||
        payment?.transactionId ||
        payment
            ?.bankReferenceNumber ||
        payment
            ?.upiTransactionId ||
        payment
            ?.cardTransactionId ||
        payment?.chequeNumber ||
        "-";

    const isReversed =
        payment?.isReversed === true ||
        paymentStatus === "REVERSED" ||
        allocationStatus === "REVERSED";

    const statusClass =
        isReversed
            ? "reversed"
            : paymentStatus
                .toLowerCase()
                .replace(
                    /_/g,
                    "-"
                );


    const normalizedAllocations =
        useMemo(
            () => {
                /*
                 * First use records returned by the allocation API.
                 */
                if (
                    Array.isArray(
                        apiAllocations
                    ) &&
                    apiAllocations.length >
                    0
                ) {
                    return apiAllocations;
                }

                /*
                 * Payment history already returns allocation records.
                 */
                if (
                    Array.isArray(
                        payment
                            ?.allocations
                    ) &&
                    payment
                        .allocations
                        .length >
                    0
                ) {
                    return payment
                        .allocations;
                }

                /*
                 * Some API responses return one allocation object.
                 */
                if (
                    payment
                        ?.allocation &&
                    typeof payment
                        .allocation ===
                    "object"
                ) {
                    return [
                        payment
                            .allocation,
                    ];
                }

                /*
                 * Final fallback for legacy records.
                 */
                const fallbackAllocatedAmount =
                    roundMoney(
                        payment
                            ?.allocatedAmount ??
                        payment
                            ?.activeAmount ??
                        0
                    );

                if (
                    fallbackAllocatedAmount <=
                    0
                ) {
                    return [];
                }

                return [
                    {
                        _id:
                            payment
                                ?.allocationId ||
                            `${paymentId}-allocation`,

                        payment:
                            paymentId,

                        document:
                            payment
                                ?.document ||
                            {
                                _id:
                                    invoice
                                        ?._id ||
                                    null,

                                documentNumber:
                                    getInvoiceNumber(
                                        invoice,
                                        payment
                                    ),

                                documentDate:
                                    invoice
                                        ?.documentDate ||
                                    null,
                            },

                        documentNumber:
                            getInvoiceNumber(
                                invoice,
                                payment
                            ),

                        allocationDate:
                            payment
                                ?.allocationDate ||
                            payment
                                ?.paymentDate ||
                            payment
                                ?.createdAt ||
                            null,

                        allocatedAmount:
                            fallbackAllocatedAmount,

                        reversedAmount:
                            roundMoney(
                                payment
                                    ?.reversedAmount ||
                                0
                            ),

                        activeAmount:
                            roundMoney(
                                payment
                                    ?.activeAmount ??
                                Math.max(
                                    fallbackAllocatedAmount -
                                    toNumber(
                                        payment
                                            ?.reversedAmount
                                    ),
                                    0
                                )
                            ),

                        status:
                            payment
                                ?.allocationStatus ||
                            (
                                payment
                                    ?.isReversed
                                    ? "REVERSED"
                                    : "ACTIVE"
                            ),
                    },
                ];
            },
            [
                apiAllocations,
                invoice,
                payment,
                paymentId,
            ]
        );

    const currentPaymentAllocations =
        useMemo(() => {
            if (
                !Array.isArray(
                    normalizedAllocations
                )
            ) {
                return [];
            }

            return normalizedAllocations.filter(
                (allocation) => {
                    const allocationPaymentId =
                        allocation?.payment?._id ||
                        allocation?.payment ||
                        allocation?.paymentId ||
                        null;

                    /*
                     * Some fallback allocation objects don't
                     * contain payment id. They belong to the
                     * currently selected payment.
                     */
                    if (!allocationPaymentId) {
                        return true;
                    }

                    return (
                        String(
                            allocationPaymentId
                        ) ===
                        String(
                            paymentId
                        )
                    );
                }
            );
        }, [
            normalizedAllocations,
            paymentId,
        ]);

    const paymentAmount =
        getPaymentAmount(
            payment
        );

    const allocatedAmount =
        getAllocatedAmount(
            payment,
            currentPaymentAllocations
        );

    const reversedAmount =
        getReversedAmount(
            payment,
            currentPaymentAllocations
        );

    /*
     * IMPORTANT:
     * activeAmount MUST be declared before
     * reversible uses it.
     */
    const activeAmount =
        roundMoney(
            Math.max(
                payment?.activeAmount ??
                (
                    allocatedAmount -
                    reversedAmount
                ),
                0
            )
        );

    /*
     * Available amount that can still be allocated.
     */
    const unallocatedAmount =
        roundMoney(
            payment?.unallocatedAmount !==
                undefined &&
                payment?.unallocatedAmount !==
                null
                ? Math.max(
                    toNumber(
                        payment.unallocatedAmount
                    ),
                    0
                )
                : payment?.availableAmount !==
                    undefined &&
                    payment?.availableAmount !==
                    null
                    ? Math.max(
                        toNumber(
                            payment.availableAmount
                        ),
                        0
                    )
                    : Math.max(
                        paymentAmount -
                        allocatedAmount,
                        0
                    )
        );

    /*
     * Payment/reversal status.
     */


    /*
     * Reverse is allowed only when:
     *
     * 1. Payment exists
     * 2. Payment isn't already reversed
     * 3. It has active allocated amount
     * 4. Parent supplied reversal handler
     */
    const reversible =
        Boolean(
            paymentId
        ) &&
        !isReversed &&
        activeAmount > 0 &&
        typeof onReverse ===
        "function";

    /*
     * Allocation is allowed only when
     * some payment amount remains available.
     */
    const canAllocate =
        Boolean(
            paymentId
        ) &&
        !isReversed &&
        unallocatedAmount > 0 &&
        typeof onAllocatePayment ===
        "function";

    if (
        !open ||
        !payment
    ) {
        return null;
    }

    /*
    |--------------------------------------------------------------------------
    | HANDLERS
    |--------------------------------------------------------------------------
    */

    const handleClose =
        () => {
            if (
                reversing
            ) {
                return;
            }

            setShowReverseForm(
                false
            );

            setReversalReason(
                ""
            );

            setReversalError(
                ""
            );

            onClose?.();
        };

    const handlePrint =
        async () => {
            /*
            |--------------------------------------------------------------------------
            | ISOLATED SAME-DOCUMENT PRINT ROOT
            |--------------------------------------------------------------------------
            |
            | The visible receipt modal stays untouched.
            |
            | For printing:
            | 1. Clone ONLY the rendered receipt dialog.
            | 2. Put that clone directly under <body>.
            | 3. Append a print <style> LAST in <head>.
            | 4. Hide every other body child with display:none.
            | 5. Force the print root and all descendants visible.
            | 6. window.print().
            | 7. Remove clone/style after print.
            |
            | This prevents:
            | - Admin layout height from pushing receipt down
            | - global modal CSS
            | - parent overflow/transform
            | - global @media print visibility rules
            |--------------------------------------------------------------------------
            */

            const printableNode =
                printRef.current;

            if (!printableNode) {
                return false;
            }

            const printRootId =
                "ckw-sipd-print-root";

            const printStyleId =
                "ckw-sipd-print-style";

            document
                .getElementById(
                    printRootId
                )
                ?.remove();

            document
                .getElementById(
                    printStyleId
                )
                ?.remove();

            const printRoot =
                document.createElement(
                    "div"
                );

            printRoot.id =
                printRootId;

            printRoot.className =
                "ckw-sipd-print-root";

            const receiptClone =
                printableNode.cloneNode(
                    true
                );

            /*
            |--------------------------------------------------------------------------
            | REMOVE SCREEN-ONLY CONTROLS FROM PRINT COPY
            |--------------------------------------------------------------------------
            */

            receiptClone
                .querySelectorAll(
                    [
                        ".ckw-sipd-header-close",
                        ".ckw-sipd-footer",
                        ".ckw-sipd-reversal-panel",
                    ].join(",")
                )
                .forEach(
                    (
                        element
                    ) => {
                        element.remove();
                    }
                );

            printRoot.appendChild(
                receiptClone
            );

            /*
            |--------------------------------------------------------------------------
            | PRINT STYLE IS APPENDED LAST
            |--------------------------------------------------------------------------
            |
            | This is intentional. Even if another global stylesheet contains
            | aggressive @media print rules, this block is later in cascade and
            | uses a unique ID selector.
            |--------------------------------------------------------------------------
            */

            const printStyle =
                document.createElement(
                    "style"
                );

            printStyle.id =
                printStyleId;

            printStyle.textContent = `
                @media print {
                    @page {
                        size: A4 portrait;
                        margin: 8mm;
                    }

                    html,
                    body {
                        display: block !important;

                        width: 100% !important;
                        height: auto !important;
                        min-height: 0 !important;
                        max-height: none !important;

                        margin: 0 !important;
                        padding: 0 !important;

                        overflow: visible !important;

                        visibility: visible !important;
                        opacity: 1 !important;

                        background: #ffffff !important;

                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }

                    body.ckw-sipd-printing
                    > *:not(#${printRootId}) {
                        display: none !important;
                    }

                    body.ckw-sipd-printing
                    > #${printRootId} {
                        display: block !important;

                        position: static !important;
                        inset: auto !important;

                        width: 100% !important;
                        max-width: none !important;
                        min-width: 0 !important;

                        height: auto !important;
                        min-height: 0 !important;
                        max-height: none !important;

                        margin: 0 !important;
                        padding: 0 !important;

                        overflow: visible !important;

                        visibility: visible !important;
                        opacity: 1 !important;

                        transform: none !important;

                        color: #172033 !important;
                        background: #ffffff !important;
                    }

                    body.ckw-sipd-printing
                    > #${printRootId},
                    body.ckw-sipd-printing
                    > #${printRootId} *,
                    body.ckw-sipd-printing
                    > #${printRootId} *::before,
                    body.ckw-sipd-printing
                    > #${printRootId} *::after {
                        visibility: visible !important;
                        opacity: 1 !important;
                    }

                    body.ckw-sipd-printing
                    > #${printRootId}
                    .ckw-sipd-dialog {
                        display: block !important;

                        position: static !important;
                        inset: auto !important;

                        width: 100% !important;
                        max-width: none !important;
                        min-width: 0 !important;

                        height: auto !important;
                        min-height: 0 !important;
                        max-height: none !important;

                        margin: 0 !important;
                        padding: 0 !important;

                        overflow: visible !important;

                        visibility: visible !important;
                        opacity: 1 !important;

                        transform: none !important;

                        border: 0 !important;
                        border-radius: 0 !important;
                        box-shadow: none !important;

                        background: #ffffff !important;
                    }

                    body.ckw-sipd-printing
                    > #${printRootId}
                    .ckw-sipd-body {
                        display: block !important;

                        width: 100% !important;

                        height: auto !important;
                        min-height: 0 !important;
                        max-height: none !important;

                        margin: 0 !important;

                        overflow: visible !important;

                        background: #ffffff !important;
                    }

                    body.ckw-sipd-printing
                    > #${printRootId}
                    .ckw-sipd-header-close,
                    body.ckw-sipd-printing
                    > #${printRootId}
                    .ckw-sipd-footer,
                    body.ckw-sipd-printing
                    > #${printRootId}
                    .ckw-sipd-reversal-panel {
                        display: none !important;
                    }

                    body.ckw-sipd-printing
                    > #${printRootId}
                    .ckw-sipd-header,
                    body.ckw-sipd-printing
                    > #${printRootId}
                    .ckw-sipd-status-bar,
                    body.ckw-sipd-printing
                    > #${printRootId}
                    .ckw-sipd-amount-card,
                    body.ckw-sipd-printing
                    > #${printRootId}
                    .ckw-sipd-business-card,
                    body.ckw-sipd-printing
                    > #${printRootId}
                    .ckw-sipd-detail-field {
                        break-inside: avoid !important;
                        page-break-inside: avoid !important;
                    }

                    body.ckw-sipd-printing
                    > #${printRootId}
                    .ckw-sipd-section {
                        break-inside: auto !important;
                        page-break-inside: auto !important;
                    }

                    body.ckw-sipd-printing
                    > #${printRootId}
                    .ckw-sipd-allocation-table-wrap {
                        width: 100% !important;
                        overflow: visible !important;
                    }

                    body.ckw-sipd-printing
                    > #${printRootId}
                    .ckw-sipd-allocation-table {
                        width: 100% !important;
                        min-width: 0 !important;
                    }

                    body.ckw-sipd-printing
                    > #${printRootId}
                    .ckw-sipd-allocation-table thead {
                        display: table-header-group !important;
                    }

                    body.ckw-sipd-printing
                    > #${printRootId}
                    .ckw-sipd-allocation-table tr {
                        break-inside: avoid !important;
                        page-break-inside: avoid !important;
                    }
                }
            `;

            document.head.appendChild(
                printStyle
            );

            /*
            |--------------------------------------------------------------------------
            | PUT PRINT ROOT FIRST UNDER BODY
            |--------------------------------------------------------------------------
            */

            document.body.insertBefore(
                printRoot,
                document.body.firstChild
            );

            document.body.classList.add(
                "ckw-sipd-printing"
            );

            let cleanedUp =
                false;

            const cleanup =
                () => {
                    if (cleanedUp) {
                        return;
                    }

                    cleanedUp =
                        true;

                    window.removeEventListener(
                        "afterprint",
                        cleanup
                    );

                    document.body.classList.remove(
                        "ckw-sipd-printing"
                    );

                    printRoot.remove();
                    printStyle.remove();
                };

            window.addEventListener(
                "afterprint",
                cleanup,
                {
                    once:
                        true,
                }
            );

            try {
                if (
                    document.fonts?.ready
                ) {
                    await document
                        .fonts
                        .ready;
                }

                const images =
                    Array.from(
                        printRoot.querySelectorAll(
                            "img"
                        )
                    );

                await Promise.all(
                    images.map(
                        (
                            image
                        ) => {
                            if (
                                image.complete
                            ) {
                                return Promise.resolve();
                            }

                            return new Promise(
                                (
                                    resolve
                                ) => {
                                    const done =
                                        () => {
                                            image.removeEventListener(
                                                "load",
                                                done
                                            );

                                            image.removeEventListener(
                                                "error",
                                                done
                                            );

                                            resolve();
                                        };

                                    image.addEventListener(
                                        "load",
                                        done
                                    );

                                    image.addEventListener(
                                        "error",
                                        done
                                    );
                                }
                            );
                        }
                    )
                );

                void printRoot.offsetHeight;

                await new Promise(
                    (
                        resolve
                    ) =>
                        window.requestAnimationFrame(
                            () =>
                                window.requestAnimationFrame(
                                    resolve
                                )
                        )
                );

                window.focus();
                window.print();

                window.setTimeout(
                    cleanup,
                    60000
                );

                return true;
            } catch (
                error
            ) {
                cleanup();

                return false;
            }
        };

    const handleConfirmReverse =
        async () => {
            const reason =
                reversalReason.trim();

            if (
                reason.length <
                3
            ) {
                setReversalError(
                    "Please enter at least 3 characters."
                );

                return;
            }

            if (
                typeof onReverse !==
                "function"
            ) {
                setReversalError(
                    "Reverse payment action is not connected."
                );

                return;
            }

            setReversalError(
                ""
            );

            try {
                await onReverse(
                    payment,
                    reason
                );
            } catch (
            error
            ) {
                setReversalError(
                    error?.message ||
                    error ||
                    "Unable to reverse payment."
                );
            }
        };

    return (
        <div
            className="ckw-sipd-overlay"
            onMouseDown={(
                event
            ) => {
                if (
                    event.target ===
                    event.currentTarget
                ) {
                    handleClose();
                }
            }}
        >
            <div
                ref={printRef}
                className="ckw-sipd-dialog"
                role="dialog"
                aria-modal="true"
                onMouseDown={(
                    event
                ) =>
                    event.stopPropagation()
                }
            >
                {/* HEADER */}

                <header className="ckw-sipd-header">
                    <span className="ckw-sipd-header-icon">
                        <FiCreditCard />
                    </span>

                    <div className="ckw-sipd-header-content">
                        <span>
                            Payment Receipt
                        </span>

                        <h2>
                            {receiptNumber}
                        </h2>

                        <p>
                            <strong className="ckw-sipd-business-name-inline">
                                {tenantLegalName}
                            </strong>

                            {" · "}

                            Payment received against{" "}

                            <strong>
                                {invoiceNumber}
                            </strong>
                        </p>
                    </div>

                    <button
                        type="button"
                        className="ckw-sipd-header-close"
                        disabled={
                            reversing
                        }
                        onClick={
                            handleClose
                        }
                    >
                        <FiX />
                    </button>
                </header>

                {/* STATUS */}

                <div className="ckw-sipd-status-bar">
                    <span
                        className={`ckw-sipd-status ckw-sipd-status-${statusClass}`}
                    >
                        {isReversed ? (
                            <>
                                <FiXCircle />
                                Reversed
                            </>
                        ) : (
                            <>
                                <FiCheckCircle />
                                {formatLabel(
                                    paymentStatus
                                )}
                            </>
                        )}
                    </span>

                    <span className="ckw-sipd-created">
                        Created{" "}
                        {formatDate(
                            createdAt,
                            true
                        )}
                    </span>
                </div>

                {/* BODY */}

                <div className="ckw-sipd-body">
                    <section className="ckw-sipd-amount-card">
                        <span>
                            Amount Received
                        </span>

                        <strong>
                            {displayCurrency(
                                paymentAmount
                            )}
                        </strong>

                        <div>
                            <span>
                                Active Amount
                            </span>

                            <strong>
                                {displayCurrency(
                                    activeAmount
                                )}
                            </strong>
                        </div>
                    </section>

                    <section className="ckw-sipd-section">
                        <h3>
                            Payment Information
                        </h3>

                        <div className="ckw-sipd-info-grid">
                            <PaymentInfoField
                                icon={
                                    <FiCalendar />
                                }
                                label="Payment Date"
                                value={formatDate(
                                    paymentDate
                                )}
                            />

                            <PaymentInfoField
                                icon={
                                    <FiCreditCard />
                                }
                                label="Payment Method"
                                value={formatLabel(
                                    paymentMethod
                                )}
                            />

                            <PaymentInfoField
                                icon={
                                    <FiHash />
                                }
                                label="Reference / Transaction ID"
                                value={
                                    referenceNumber
                                }
                            />

                            <PaymentInfoField
                                icon={
                                    <FiFileText />
                                }
                                label="Receipt Number"
                                value={
                                    receiptNumber
                                }
                            />

                            <PaymentInfoField
                                icon={
                                    <FiFileText />
                                }
                                label="Allocation Status"
                                value={formatLabel(
                                    allocationStatus
                                )}
                            />

                            <PaymentInfoField
                                icon={
                                    <FiCreditCard />
                                }
                                label="Payment Status"
                                value={formatLabel(
                                    paymentStatus
                                )}
                            />
                        </div>
                    </section>

                    <section className="ckw-sipd-section ckw-sipd-business-section">
                        <h3>
                            Business Details
                        </h3>

                        <div className="ckw-sipd-business-card">
                            <div className="ckw-sipd-business-logo">
                                {tenantLogo ? (
                                    <img
                                        src={
                                            tenantLogo
                                        }
                                        alt={
                                            tenantLegalName
                                        }
                                    />
                                ) : (
                                    <span>
                                        {tenantLegalName
                                            .charAt(
                                                0
                                            )}
                                    </span>
                                )}
                            </div>

                            <div className="ckw-sipd-business-content">
                                <strong>
                                    {tenantLegalName}
                                </strong>

                                <span>
                                    {tenantAddressText}
                                </span>
                            </div>
                        </div>

                        <div className="ckw-sipd-info-grid ckw-sipd-business-info-grid">
                            <PaymentInfoField
                                icon={
                                    <FiUser />
                                }
                                label="Business Mobile"
                                value={
                                    tenantMobile
                                }
                            />

                            <PaymentInfoField
                                icon={
                                    <FiFileText />
                                }
                                label="Business Email"
                                value={
                                    tenantEmail
                                }
                            />

                            <PaymentInfoField
                                icon={
                                    <FiFileText />
                                }
                                label="GSTIN"
                                value={
                                    tenantGstin
                                }
                            />

                            <PaymentInfoField
                                icon={
                                    <FiHash />
                                }
                                label="PAN"
                                value={
                                    tenantPan
                                }
                            />
                        </div>
                    </section>

                    <section className="ckw-sipd-section">
                        <h3>
                            Invoice & Customer
                        </h3>

                        <div className="ckw-sipd-info-grid">
                            <PaymentInfoField
                                icon={
                                    <FiFileText />
                                }
                                label="Invoice Number"
                                value={
                                    invoiceNumber
                                }
                            />

                            <PaymentInfoField
                                icon={
                                    <FiCalendar />
                                }
                                label="Invoice Date"
                                value={formatDate(
                                    invoice
                                        ?.documentDate ||
                                    payment
                                        ?.document
                                        ?.documentDate
                                )}
                            />

                            <PaymentInfoField
                                icon={
                                    <FiUser />
                                }
                                label="Customer"
                                value={
                                    customerName
                                }
                            />

                            <PaymentInfoField
                                icon={
                                    <FiUser />
                                }
                                label="Mobile"
                                value={
                                    customerMobile
                                }
                            />
                        </div>
                    </section>

                    <section className="ckw-sipd-section">
                        <h3>
                            Payment Allocation
                        </h3>

                        <div className="ckw-sipd-summary-grid">
                            <div>
                                <span>
                                    Payment
                                </span>

                                <strong>
                                    {displayCurrency(
                                        paymentAmount
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Allocated
                                </span>

                                <strong>
                                    {displayCurrency(
                                        allocatedAmount
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Reversed
                                </span>

                                <strong>
                                    {displayCurrency(
                                        reversedAmount
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Available
                                </span>

                                <strong>
                                    {displayCurrency(
                                        unallocatedAmount
                                    )}
                                </strong>
                            </div>
                        </div>

                        {loadingAllocations &&
                            currentPaymentAllocations.length ===
                            0 && (
                                <div className="ckw-sipd-allocation-message">
                                    <FiLoader className="ckw-sipd-spinner" />

                                    Loading allocated invoices...
                                </div>
                            )}

                        {!loadingAllocations &&
                            allocationError &&
                            currentPaymentAllocations.length ===
                            0 &&
                            allocatedAmount <=
                            0 && (
                                <div className="ckw-sipd-allocation-error">
                                    <FiAlertCircle />

                                    {allocationError}
                                </div>
                            )}

                        {!loadingAllocations &&
                            currentPaymentAllocations.length ===
                            0 &&
                            allocatedAmount <=
                            0 && (
                                <div className="ckw-sipd-allocation-empty">
                                    <FiFileText />

                                    <div>
                                        <strong>
                                            No allocation recorded
                                        </strong>

                                        <span>
                                            This payment has not been allocated to an invoice yet.
                                        </span>
                                    </div>
                                </div>
                            )}

                        {currentPaymentAllocations.length >
                            0 && (
                                <div className="ckw-sipd-allocation-table-wrap">
                                    <table className="ckw-sipd-allocation-table">
                                        <thead>
                                            <tr>
                                                <th>
                                                    Invoice
                                                </th>

                                                <th>
                                                    Date
                                                </th>

                                                <th>
                                                    Status
                                                </th>

                                                <th className="ckw-sipd-text-right">
                                                    Allocated
                                                </th>

                                                <th className="ckw-sipd-text-right">
                                                    Active
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {currentPaymentAllocations.map(
                                                (
                                                    allocation,
                                                    index
                                                ) => (
                                                    <tr
                                                        key={
                                                            allocation
                                                                ?._id ||
                                                            index
                                                        }
                                                    >
                                                        <td>
                                                            <strong>
                                                                {allocation
                                                                    ?.document
                                                                    ?.documentNumber ||
                                                                    allocation
                                                                        ?.documentNumber ||
                                                                    payment
                                                                        ?.document
                                                                        ?.documentNumber ||
                                                                    payment
                                                                        ?.documentNumber ||
                                                                    invoiceNumber ||
                                                                    "-"}
                                                            </strong>
                                                        </td>

                                                        <td>
                                                            {formatDate(
                                                                allocation
                                                                    ?.allocationDate ||
                                                                allocation
                                                                    ?.createdAt ||
                                                                payment
                                                                    ?.allocationDate ||
                                                                paymentDate
                                                            )}
                                                        </td>

                                                        <td>
                                                            <span
                                                                className={`ckw-sipd-allocation-status ckw-sipd-allocation-status-${String(
                                                                    allocation
                                                                        ?.status ||
                                                                    "ACTIVE"
                                                                )
                                                                    .toLowerCase()
                                                                    .replace(
                                                                        /_/g,
                                                                        "-"
                                                                    )}`}
                                                            >
                                                                {formatLabel(
                                                                    allocation
                                                                        ?.status ||
                                                                    "ACTIVE"
                                                                )}
                                                            </span>
                                                        </td>

                                                        <td className="ckw-sipd-text-right">
                                                            {displayCurrency(
                                                                allocation
                                                                    ?.allocatedAmount
                                                            )}
                                                        </td>

                                                        <td className="ckw-sipd-text-right ckw-sipd-active-value">
                                                            {displayCurrency(
                                                                allocation
                                                                    ?.activeAmount ??
                                                                Math.max(
                                                                    toNumber(
                                                                        allocation
                                                                            ?.allocatedAmount
                                                                    ) -
                                                                    toNumber(
                                                                        allocation
                                                                            ?.reversedAmount
                                                                    ),
                                                                    0
                                                                )
                                                            )}
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                    </section>

                    {/* REVERSAL FORM */}

                </div>

                {/* FOOTER */}

                <footer className="ckw-sipd-footer">
                    <div className="ckw-sipd-footer-left">
                        {!showReverseForm &&
                            unallocatedAmount <=
                            0 &&
                            !isReversed && (
                                <span className="ckw-sipd-fully-allocated-note">
                                    <FiCheckCircle />

                                    This payment is fully allocated.
                                </span>
                            )}

                        {isReversed && (
                            <span className="ckw-sipd-reversed-note">
                                <FiXCircle />

                                This payment has been reversed.
                            </span>
                        )}
                    </div>

                    <div className="ckw-sipd-footer-actions">
                        <button
                            type="button"
                            className="ckw-sipd-button ckw-sipd-close-button"
                            disabled={
                                reversing
                            }
                            onClick={
                                handleClose
                            }
                        >
                            Close
                        </button>

                        <button
                            type="button"
                            className="ckw-sipd-button ckw-sipd-print-button"
                            disabled={
                                reversing
                            }
                            onClick={
                                handlePrint
                            }
                        >
                            <FiPrinter />

                            Print Receipt
                        </button>

                        {!showReverseForm &&
                            !isReversed && (
                                <button
                                    type="button"
                                    className="ckw-sipd-button ckw-sipd-allocate-button"
                                    disabled={
                                        reversing ||
                                        !canAllocate
                                    }
                                    title={
                                        unallocatedAmount <=
                                            0
                                            ? "This payment is already fully allocated."
                                            : !paymentId
                                                ? "Payment ID is missing."
                                                : typeof onAllocatePayment !==
                                                    "function"
                                                    ? "Allocate payment action is not connected."
                                                    : `Allocate ${displayCurrency(
                                                        unallocatedAmount
                                                    )}`
                                    }
                                    onClick={() => {
                                        if (
                                            !canAllocate
                                        ) {
                                            return;
                                        }

                                        onAllocatePayment(
                                            {
                                                ...payment,

                                                _id:
                                                    paymentId,

                                                paymentId,

                                                amount:
                                                    paymentAmount,

                                                paymentAmount,

                                                allocatedAmount,

                                                reversedAmount,

                                                activeAmount,

                                                unallocatedAmount,

                                                availableAmount:
                                                    unallocatedAmount,

                                                allocations:
                                                    currentPaymentAllocations,
                                            }
                                        );
                                    }}
                                >
                                    <FiCreditCard />

                                    {unallocatedAmount >
                                        0
                                        ? `Allocate ${displayCurrency(
                                            unallocatedAmount
                                        )}`
                                        : "Fully Allocated"}
                                </button>
                            )}

                        {reversible &&
                            !showReverseForm && (
                                <button
                                    type="button"
                                    className="ckw-sipd-button ckw-sipd-reverse-button"
                                    disabled={
                                        reversing
                                    }
                                    title="Reverse this payment"
                                    onClick={() => {
                                        setReversalReason(
                                            ""
                                        );

                                        setReversalError(
                                            ""
                                        );

                                        setShowReverseForm(
                                            true
                                        );
                                    }}
                                >
                                    <FiRefreshCw />

                                    Reverse Payment
                                </button>
                            )}
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default
    SaleInvoicePaymentDetailsDialog;